import React, { useEffect, useMemo, useState } from 'react';
import { calculateHospitalDistances, createHospital, createOperatingRoom, updateHospital } from '../services/api.ts';
import type { Hospital, HospitalUpdate } from '../services/api.ts';
import Modal from './Modal.tsx';
import { colorForHospital } from './hospitalColors.ts';
import { timezoneOptionsFor } from './hospitalTimezones.ts';

// Create or edit one hospital in a dialog: name, address, coordinates, time zone, board colour and site type.
// Saving goes through services/api.ts, which marks the cached hospital list stale, so the table and the
// Board refresh on their own. When the coordinates change, the hospital-distance matrix the allocators
// use is recalculated too — it is computed from coordinates only, and nothing else recomputes it.

export interface HospitalFormValues {
  name: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  /** Kept as typed text so an empty box means "no coordinate". */
  latitude: string;
  longitude: string;
  useCustomColor: boolean;
  color: string;
  isSurgeryCenter: boolean;
  isChildrensHospital: boolean;
  /** An IANA zone name, or '' for "not set". */
  timezone: string;
  /** Create only: generic rooms (OR 1 … OR N) to add with the new hospital. */
  numRooms: string;
}

export const EMPTY_HOSPITAL_FORM: HospitalFormValues = {
  name: '', street: '', city: '', state: '', zipCode: '', country: 'USA',
  latitude: '', longitude: '',
  useCustomColor: false, color: '#3b82f6',
  isSurgeryCenter: false, isChildrensHospital: false,
  timezone: '',
  numRooms: '',
};

export const hospitalFormFromHospital = (hospital: Hospital): HospitalFormValues => ({
  name: hospital.name,
  street: hospital.address?.street ?? '',
  city: hospital.address?.city ?? '',
  state: hospital.address?.state ?? '',
  zipCode: hospital.address?.zipCode ?? '',
  country: hospital.address?.country ?? '',
  latitude: hospital.latitude !== undefined ? String(hospital.latitude) : '',
  longitude: hospital.longitude !== undefined ? String(hospital.longitude) : '',
  useCustomColor: !!hospital.color,
  color: colorForHospital(hospital),
  isSurgeryCenter: !!hospital.isSurgeryCenter,
  isChildrensHospital: !!hospital.isChildrensHospital,
  timezone: hospital.timezone ?? '',
  numRooms: '',
});

const coordinate = (text: string): number | null => (text.trim() === '' ? null : Number(text));

/**
 * The PUT body for an edit. Name, address and coordinates are always sent (an emptied coordinate box
 * clears it). Colour, the site-type flags and the time zone are sent only when the admin actually changed them:
 * a never-set site-type flag means something different from an explicit "no" (see services/api.ts),
 * so merely opening and saving the dialog must never pin one.
 */
export const hospitalUpdateFromForm = (hospital: Hospital, initial: HospitalFormValues, form: HospitalFormValues): HospitalUpdate => {
  const update: HospitalUpdate = {
    name: form.name.trim(),
    address: {
      street: form.street.trim(), city: form.city.trim(), state: form.state.trim(),
      zipCode: form.zipCode.trim(), country: form.country.trim(),
    },
    latitude: coordinate(form.latitude),
    longitude: coordinate(form.longitude),
  };
  if (form.useCustomColor && (!initial.useCustomColor || form.color !== initial.color)) update.color = form.color;
  if (!form.useCustomColor && hospital.color) update.color = '';
  if (form.isSurgeryCenter !== initial.isSurgeryCenter) update.isSurgeryCenter = form.isSurgeryCenter;
  if (form.isChildrensHospital !== initial.isChildrensHospital) update.isChildrensHospital = form.isChildrensHospital;
  // Untouched, the zone is omitted so the save can never wipe it; "Not set" sends null to clear it.
  if (form.timezone !== initial.timezone) update.timezone = form.timezone || null;
  return update;
};

/** Whether the saved coordinates differ from the ones the hospital had, so distances need recomputing. */
export const coordinatesChanged = (initial: HospitalFormValues, form: HospitalFormValues): boolean =>
  coordinate(initial.latitude) !== coordinate(form.latitude) || coordinate(initial.longitude) !== coordinate(form.longitude);

interface HospitalFormDialogProps {
  open: boolean;
  /** The hospital being edited, or null to create a new one. */
  hospital: Hospital | null;
  onClose: () => void;
  /** Called after a successful save with a message for the page to show. */
  onSaved: (message: string, type?: 'success' | 'warning') => void;
}

const FORM_ID = 'hospital-form';
const hint: React.CSSProperties = { display: 'block', marginTop: '4px', color: 'var(--text-muted)' };
const inlineCheck: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'normal', cursor: 'pointer' };

const HospitalFormDialog: React.FC<HospitalFormDialogProps> = ({ open, hospital, onClose, onSaved }) => {
  const editing = !!hospital;
  const initial = useMemo(() => (hospital ? hospitalFormFromHospital(hospital) : EMPTY_HOSPITAL_FORM), [hospital]);
  const [form, setForm] = useState<HospitalFormValues>(initial);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Each time the dialog opens (or switches to a different hospital) it starts from that record.
  useEffect(() => {
    if (open) {
      setForm(initial);
      setErrorMessage(null);
    }
  }, [open, initial]);

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const set = <K extends keyof HospitalFormValues>(key: K, value: HospitalFormValues[K]) => setForm(f => ({ ...f, [key]: value }));
  const text = (key: keyof HospitalFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => set(key, e.target.value as any);

  const recalculateDistances = async (): Promise<boolean> => {
    try {
      await calculateHospitalDistances();
      return true;
    } catch (error) {
      console.error('Failed to recalculate hospital distances:', error);
      return false;
    }
  };

  const save = async () => {
    const name = form.name.trim();
    if (editing && hospital) {
      await updateHospital(hospital.hospitalId, hospitalUpdateFromForm(hospital, initial, form));
      if (!coordinatesChanged(initial, form)) return onSaved(`${name} updated.`);
      return (await recalculateDistances())
        ? onSaved(`${name} updated. Hospital distances recalculated.`)
        : onSaved(`${name} updated, but hospital distances could not be recalculated. Try again later.`, 'warning');
    }

    const latitude = coordinate(form.latitude);
    const longitude = coordinate(form.longitude);
    const res: any = await createHospital({
      name,
      address: {
        street: form.street.trim(), city: form.city.trim(), state: form.state.trim(),
        zipCode: form.zipCode.trim(), country: form.country.trim(),
      },
      ...(latitude !== null && { latitude }),
      ...(longitude !== null && { longitude }),
      ...(form.useCustomColor && { color: form.color }),
      ...(form.timezone && { timezone: form.timezone }),
      // Always send both site-type flags on create, so a brand-new hospital is explicitly classified
      // from day one and never falls back to the deprecated name heuristic.
      isSurgeryCenter: form.isSurgeryCenter,
      isChildrensHospital: form.isChildrensHospital,
    });
    const created = res?.hospital;
    const n = Number(form.numRooms);
    let madeRooms = 0;
    if (created?.hospitalId && Number.isInteger(n) && n > 0) {
      for (let i = 1; i <= n; i++) {
        await createOperatingRoom({ roomNumber: `OR ${i}`, name: `OR ${i}`, hospitalId: created.hospitalId });
        madeRooms++;
      }
    }
    const roomsNote = madeRooms ? ` with ${madeRooms} operating room${madeRooms > 1 ? 's' : ''}` : '';
    if (latitude === null || longitude === null) return onSaved(`${name} created${roomsNote}.`);
    return (await recalculateDistances())
      ? onSaved(`${name} created${roomsNote}. Hospital distances recalculated.`)
      : onSaved(`${name} created${roomsNote}, but hospital distances could not be recalculated. Try again later.`, 'warning');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    try {
      await save();
      onClose();
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.error || `Failed to ${editing ? 'update' : 'create'} hospital`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!saving) onClose(); }}
      title={editing ? 'Edit hospital' : 'Create hospital'}
      closeOnBackdrop={!dirty && !saving}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form={FORM_ID} className="btn btn-primary" disabled={saving}>
            {saving ? (editing ? 'Saving…' : 'Creating…') : (editing ? 'Save changes' : 'Create hospital')}
          </button>
        </>
      }
    >
      {errorMessage && <div className="alert alert-error" role="alert">{errorMessage}</div>}
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="hospital-name">Hospital Name</label>
          <input type="text" id="hospital-name" value={form.name} onChange={text('name')} required placeholder="e.g., St. Mary's Hospital" />
        </div>

        <h3>Address</h3>
        <div className="form-group">
          <label htmlFor="hospital-street">Street Address</label>
          <input type="text" id="hospital-street" value={form.street} onChange={text('street')} required placeholder="790 Fairhaven Street" />
        </div>
        <div className="grid grid-2">
          <div className="form-group">
            <label htmlFor="hospital-city">City</label>
            <input type="text" id="hospital-city" value={form.city} onChange={text('city')} required placeholder="New York" />
          </div>
          <div className="form-group">
            <label htmlFor="hospital-state">State</label>
            <input type="text" id="hospital-state" value={form.state} onChange={text('state')} required placeholder="NY" />
          </div>
        </div>
        <div className="grid grid-2">
          <div className="form-group">
            <label htmlFor="hospital-zip">ZIP Code</label>
            <input type="text" id="hospital-zip" value={form.zipCode} onChange={text('zipCode')} required placeholder="10001" />
          </div>
          <div className="form-group">
            <label htmlFor="hospital-country">Country</label>
            <input type="text" id="hospital-country" value={form.country} onChange={text('country')} required placeholder="USA" />
          </div>
        </div>
        <div className="grid grid-2">
          <div className="form-group">
            <label htmlFor="hospital-latitude">Latitude</label>
            <input type="number" id="hospital-latitude" value={form.latitude} onChange={text('latitude')} step="any" min="-90" max="90" placeholder="e.g., 40.7128" />
            <small style={hint}>Optional. Between -90 and 90.</small>
          </div>
          <div className="form-group">
            <label htmlFor="hospital-longitude">Longitude</label>
            <input type="number" id="hospital-longitude" value={form.longitude} onChange={text('longitude')} step="any" min="-180" max="180" placeholder="e.g., -74.0060" />
            <small style={hint}>Optional. Between -180 and 180.</small>
          </div>
        </div>
        <small style={{ ...hint, marginTop: '-8px', marginBottom: '16px' }}>
          Coordinates set how far apart sites are for allocation. Changing them recalculates hospital distances.
        </small>

        <div className="form-group">
          <label htmlFor="hospital-timezone">Time Zone</label>
          <select id="hospital-timezone" className="form-control" value={form.timezone} onChange={e => set('timezone', e.target.value)} style={{ maxWidth: '360px' }}>
            <option value="">Not set</option>
            {timezoneOptionsFor(initial.timezone || undefined).map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <small style={hint}>
            Optional. Used to place this site’s local times from schedule feeds (e.g. a 07:30 case) at the right moment,
            including across daylight-saving changes.
          </small>
        </div>

        {!editing && (
          <div className="form-group">
            <label htmlFor="hospital-num-rooms">Number of Operating Rooms</label>
            <input type="number" id="hospital-num-rooms" min="0" value={form.numRooms} onChange={text('numRooms')} placeholder="e.g., 8" style={{ maxWidth: '160px' }} />
            <small style={hint}>Optional. Creates that many rooms (OR 1 … OR N). Add typed rooms (Endo, C/S, MRI…) afterwards.</small>
          </div>
        )}

        <div className="form-group">
          <label>Board Color</label>
          <label style={inlineCheck}>
            <input type="checkbox" checked={form.useCustomColor} onChange={e => set('useCustomColor', e.target.checked)} style={{ width: 'auto' }} />
            Pick a custom color (otherwise a distinct one is auto-assigned)
          </label>
          {form.useCustomColor && (
            <input
              type="color"
              value={form.color}
              onChange={e => set('color', e.target.value)}
              aria-label="Board color"
              style={{ width: '48px', height: '30px', padding: 0, border: '1px solid var(--border-soft)', borderRadius: '6px', cursor: 'pointer', background: 'none', marginTop: '8px' }}
            />
          )}
        </div>

        <div className="form-group">
          <label>Site Type</label>
          <label style={inlineCheck}>
            <input type="checkbox" checked={form.isSurgeryCenter} onChange={e => set('isSurgeryCenter', e.target.checked)} style={{ width: 'auto' }} />
            Surgery center
          </label>
          {/* The two never-set states mean different things — see services/api.ts HospitalUpdate. */}
          {editing && hospital?.isSurgeryCenter === undefined && form.isSurgeryCenter === initial.isSurgeryCenter && (
            <small style={hint}>Not set — the allocator guesses surgery-center status from the name until you save a choice here.</small>
          )}
          <label style={inlineCheck}>
            <input type="checkbox" checked={form.isChildrensHospital} onChange={e => set('isChildrensHospital', e.target.checked)} style={{ width: 'auto' }} />
            Children’s hospital
          </label>
          {editing && hospital?.isChildrensHospital === undefined && form.isChildrensHospital === initial.isChildrensHospital && (
            <small style={hint}>Not set — staffed as a general adult site, whatever its name.</small>
          )}
          <small style={hint}>
            Drives allocation: surgery centers are staffed first with travel-aware provider matching, and
            children’s hospitals may only be supervised by pediatric anesthesiologists.
          </small>
        </div>
      </form>
    </Modal>
  );
};

export default HospitalFormDialog;
